import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiTrendingUp,
  FiPackage,
  FiShoppingCart,
  FiDollarSign,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
} from "react-icons/fi";

import {
  getAdminBands,
  getBandById,
  getBandAnalytics,
  getBandProductAnalytics,
  getProductsByBand,
} from "../../../store/slices/bands/asyncThunks";

import {
  selectBands,
  selectBandById,
  selectBandAnalytics,
  selectBandProductAnalytics,
  selectBandProducts,
  clearBandById,
  clearBandAnalytics,
  clearBandProductAnalytics,
} from "../../../store/slices/bands/slice";
import { getBandViewStats } from "../../../store/slices/viewTracking/asyncThunks";
import { selectViewStats } from "../../../store/slices/viewTracking/slice";

import {
  BandGenre,
  BandGrid,
  BandName,
  BandStatCard,
  ChartContainer,
  ChartTitle,
  PageSection,
  StatCard,
  StatIcon,
  StatLabel,
  StatsGrid,
  StatValue,
  AnalyticsContainer,
  ProductStats,
  TopProductsContainer,
  InventoryGrid,
  InventoryCard,
  InventoryHeader,
  InventoryIcon,
  InventoryTitle,
  InventoryStats,
  StockAlert,
  AlertIcon,
  ProductItem,
  ProductName,
  ProductCategory,
  ChartsGrid,
  PaginationContainer,
  PaginationButton,
  PageInfo,
  SizeStockDisplay,
  SizeStockTitle,
  SizeStockList,
  SizeStockBadge,
  PriceContainer,
  OriginalPrice,
  SalePrice,
  EditIcon,
} from "./AdminAnalyticsPage.styles";

// Import search styles from AdminBandsPage
import {
  SearchContainer,
  SearchInput,
} from "../AdminBandsPage/AdminBandsPage.styles";

import {
  formatPrice,
  getProductSizeStocksWithAlerts,
} from "../../../utils/utils";
import AdminContainer from "../../../components/utils/AdminContainer";
import Loader from "../../../components/utils/Loader";
import { useNavigate } from "react-router-dom";

const AdminAnalyticsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const bands = useSelector(selectBands);
  const selectedBandData = useSelector(selectBandById);
  const bandAnalytics = useSelector(selectBandAnalytics);
  const bandProductAnalytics = useSelector(selectBandProductAnalytics);
  const bandProducts = useSelector(selectBandProducts);
  const viewStats = useSelector(selectViewStats);

  // Local state
  const [selectedBand, setSelectedBand] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const bandsPerPage = 12; // Increased from 8 to 12

  // Load bands with pagination and search
  useEffect(() => {
    dispatch(
      getAdminBands({
        page: currentPage,
        limit: bandsPerPage,
        search: searchTerm,
      })
    );
  }, [dispatch, currentPage, searchTerm]);

  // Reset to page 1 when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination functions
  const totalPages = bands?.pagination?.totalPages || 1;
  const totalBands = bands?.pagination?.totalBands || 0;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePrevious = () => {
    if (canGoPrevious) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const selectBand = async (bandId) => {
    const band = bands.data.find((b) => b.id === bandId);
    setSelectedBand(band);

    // Clear previous data
    dispatch(clearBandById());
    dispatch(clearBandAnalytics());
    dispatch(clearBandProductAnalytics());

    try {
      // Fetch band details and analytics
      await Promise.all([
        dispatch(getBandById(bandId)),
        dispatch(getBandAnalytics(bandId)),
        dispatch(getBandProductAnalytics({ id: bandId, limit: 10 })),
        dispatch(getProductsByBand({ id: bandId, page: 1, limit: 20 })),
        dispatch(getBandViewStats(bandId)),
      ]);
    } catch (error) {
      console.error("Error fetching band data:", error);
    }
  };

  const getTopProducts = (products) => {
    if (!products || products.length === 0) return [];
    return Array.from(products)
      .sort((a, b) => (b.totalQuantitySold || 0) - (a.totalQuantitySold || 0))
      .slice(0, 5);
  };

  const getStockStatus = (product) => {
    const productWithStock = bandProducts.data.find(
      (p) => p.name === product.productName
    );

    if (!productWithStock) {
      return { status: "unknown", color: "#888", message: "Nepoznato stanje" };
    }

    const stock = productWithStock.stockQuantity || 0;

    if (stock === 0)
      return {
        status: "out",
        color: "#e74c3c",
        message: "Nema na stanju",
      };
    if (stock <= 10)
      return {
        status: "low",
        color: "#f39c12",
        message: `Nizak nivo - ${stock} kom`,
      };
    return {
      status: "good",
      color: "#27ae60",
      message: `Dobro stanje - ${stock} kom`,
    };
  };

  const handleProductClick = (product) => {
    // Navigacija sa state parametrom
    navigate("/admin-panel/proizvodi", {
      state: {
        openEditModal: true,
        productId: product.productId,
      },
    });
  };

  return (
    <AdminContainer>
      <PageSection>
        <AnalyticsContainer>
          <ChartTitle>Izaberite Bend za Analitiku</ChartTitle>

          {/* Search Input */}
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Pretraži bendove po imenu, žanru, gradu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {bands.loading ? (
            <Loader title={"bendova"} />
          ) : bands.data && bands.data.length > 0 ? (
            <BandGrid>
              {bands.data.map((band) => (
                <BandStatCard
                  key={band.id}
                  $selected={selectedBand?.id === band.id}
                  $backgroundImage={band.mainImage}
                  onClick={() => {
                    if (selectedBand?.id !== band.id) {
                      selectBand(band.id);
                    }
                  }}
                >
                  <BandName>{band.name}</BandName>
                  <BandGenre>{band.genre || "Nepoznat žanr"}</BandGenre>
                  <ProductStats>
                    {band.numOfProducts === 1
                      ? `${band.numOfProducts} proizvod`
                      : `${band.numOfProducts} proizvoda`}{" "}
                  </ProductStats>
                </BandStatCard>
              ))}
            </BandGrid>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: "#888",
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "10px",
                border: "1px solid #333",
              }}
            >
              <h3>Nema rezultata</h3>
              <p>
                {searchTerm
                  ? `Nema bendova koji odgovaraju pretragji "${searchTerm}"`
                  : "Nema dostupnih bendova"}
              </p>
            </div>
          )}

          {/* Pagination Controls */}
          {!bands.loading && totalBands > bandsPerPage && (
            <PaginationContainer>
              <PaginationButton
                onClick={handlePrevious}
                disabled={!canGoPrevious}
              >
                <FiChevronLeft />
                Prethodna
              </PaginationButton>

              <PageInfo>
                Strana <span className="current-page">{currentPage}</span> od{" "}
                {totalPages}
                <div style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
                  {searchTerm
                    ? `Pronađeno ${bands.data?.length || 0} bendova`
                    : `Ukupno ${totalBands} bendova`}
                </div>
              </PageInfo>

              <PaginationButton onClick={handleNext} disabled={!canGoNext}>
                Sledeća
                <FiChevronRight />
              </PaginationButton>
            </PaginationContainer>
          )}
        </AnalyticsContainer>

        {selectedBand && (
          <AnalyticsContainer>
            <ChartTitle>Analitika za {selectedBand.name}</ChartTitle>

            {bandAnalytics.loading || selectedBandData.loading ? (
              <Loader title={"analitike"} />
            ) : bandAnalytics.data ? (
              <>
                <StatsGrid>
                  <StatCard>
                    <StatIcon>
                      <FiTrendingUp />
                    </StatIcon>
                    <StatValue>
                      {formatPrice(bandAnalytics.data.totalRevenue / 117, true)}
                    </StatValue>
                    <StatLabel $eur>
                      {formatPrice(bandAnalytics.data.totalRevenue)}
                    </StatLabel>
                    <StatLabel>Ukupan prihod</StatLabel>
                    <StatLabel style={{ fontSize: "0.9rem", color: "#ff6b35" }}>
                      Bend deo:{" "}
                      {formatPrice(
                        (bandAnalytics.data.bandTotalRevenue || 0) / 117,
                        true
                      )}{" "}
                      = {formatPrice(bandAnalytics.data.bandTotalRevenue || 0)}
                    </StatLabel>
                    <StatLabel
                      style={{
                        fontSize: "0.9rem",
                        color: "#28a745",
                        marginTop: "-0.5rem",
                      }}
                    >
                      Naš deo:{" "}
                      {formatPrice(
                        (bandAnalytics.data.totalRevenue -
                          bandAnalytics.data.bandTotalRevenue) /
                          117,
                        true
                      )}{" "}
                      ={" "}
                      {formatPrice(
                        bandAnalytics.data.totalRevenue -
                          bandAnalytics.data.bandTotalRevenue
                      )}
                    </StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatIcon>
                      <FiDollarSign />
                    </StatIcon>
                    <StatValue>
                      {formatPrice(
                        bandAnalytics.data.currentMonthRevenue / 117,
                        true
                      )}
                    </StatValue>
                    <StatLabel $eur>
                      {formatPrice(bandAnalytics.data.currentMonthRevenue)}
                    </StatLabel>
                    <StatLabel>Zarada ovog meseca</StatLabel>
                    <StatLabel style={{ fontSize: "0.9rem", color: "#ff6b35" }}>
                      Bend deo:{" "}
                      {formatPrice(
                        (bandAnalytics.data.bandCurrentMonthRevenue || 0) / 117,
                        true
                      )}{" "}
                      ={" "}
                      {formatPrice(
                        bandAnalytics.data.bandCurrentMonthRevenue || 0
                      )}
                    </StatLabel>
                    <StatLabel
                      style={{
                        fontSize: "0.9rem",
                        color: "#28a745",
                        marginTop: "-0.5rem",
                      }}
                    >
                      Naš deo:{" "}
                      {formatPrice(
                        (bandAnalytics.data.currentMonthRevenue -
                          bandAnalytics.data.bandCurrentMonthRevenue) /
                          117,
                        true
                      )}{" "}
                      ={" "}
                      {formatPrice(
                        bandAnalytics.data.currentMonthRevenue -
                          bandAnalytics.data.bandCurrentMonthRevenue
                      )}
                    </StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatIcon>
                      <FiPackage />
                    </StatIcon>
                    <StatValue>{bandAnalytics.data.totalProducts}</StatValue>
                    <StatLabel>Broj Proizvoda</StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatIcon>
                      <FiShoppingCart />
                    </StatIcon>
                    <StatValue>{bandAnalytics.data.totalOrders}</StatValue>
                    <StatLabel>Ukupno Porudžbina</StatLabel>
                  </StatCard>

                  <StatCard>
                    <StatIcon>
                      <FiEye />
                    </StatIcon>
                    <StatValue>
                      {viewStats.data?.viewStats?.totalViews || 0}
                    </StatValue>
                    <StatLabel>Ukupno Pregleda</StatLabel>
                  </StatCard>
                </StatsGrid>

                <ChartsGrid>
                  <ChartContainer>
                    <ChartTitle>Najprodavaniji Proizvodi</ChartTitle>
                    <TopProductsContainer>
                      {bandAnalytics.data.bestSellingProducts &&
                        getTopProducts(
                          bandAnalytics.data.bestSellingProducts
                        ).map((product, index) => (
                          <ProductItem key={product.productId || index}>
                            <ProductName>
                              #{index + 1} {product.productName}
                            </ProductName>
                            <ProductCategory>
                              Prodato: {product.totalQuantity} kom
                            </ProductCategory>
                            <ProductStats>
                              Prihod: {formatPrice(product.totalRevenue)}
                            </ProductStats>
                          </ProductItem>
                        ))}
                      {(!bandAnalytics.data.bestSellingProducts ||
                        bandAnalytics.data.bestSellingProducts.length ===
                          0) && (
                        <ProductItem>
                          <ProductName>Nema podataka o prodaji</ProductName>
                          <ProductCategory>
                            Bend još uvek nema prodaje
                          </ProductCategory>
                        </ProductItem>
                      )}
                    </TopProductsContainer>
                  </ChartContainer>

                  <ChartContainer>
                    <ChartTitle>Pregled Proizvoda</ChartTitle>
                    <InventoryGrid>
                      {bandProductAnalytics.loading ? (
                        <Loader title={"proizvoda"} />
                      ) : bandProductAnalytics.data.products &&
                        bandProductAnalytics.data.products.length > 0 ? (
                        bandProductAnalytics.data.products.map((product) => {
                          const stockStatus = getStockStatus(product);

                          return (
                            <InventoryCard
                              key={product.id}
                              $status={product.status}
                            >
                              <InventoryHeader>
                                <InventoryIcon $status={stockStatus.status}>
                                  {stockStatus.status === "out" ||
                                  stockStatus.status === "low" ? (
                                    <FiAlertTriangle />
                                  ) : (
                                    <FiCheckCircle />
                                  )}
                                </InventoryIcon>
                                <InventoryTitle>
                                  {product.productName}
                                </InventoryTitle>
                                <EditIcon
                                  onClick={() => {
                                    handleProductClick(product);
                                  }}
                                />
                              </InventoryHeader>

                              <InventoryStats>
                                <div>
                                  Cena:{" "}
                                  {product.salePrice ? (
                                    <PriceContainer>
                                      <OriginalPrice>
                                        {formatPrice(product.price)}
                                      </OriginalPrice>
                                      <SalePrice>
                                        {formatPrice(product.salePrice)}
                                      </SalePrice>
                                    </PriceContainer>
                                  ) : (
                                    <strong>
                                      {formatPrice(product.price)}
                                    </strong>
                                  )}
                                </div>
                                <div>
                                  Kategorija:{" "}
                                  <strong>
                                    {product.categoryName || "N/A"}
                                  </strong>
                                </div>
                                {product.totalQuantitySold && (
                                  <div>
                                    Prodato:{" "}
                                    <strong>
                                      {product.totalQuantitySold} kom
                                    </strong>
                                  </div>
                                )}
                              </InventoryStats>

                              {getProductSizeStocksWithAlerts(product).length >
                                0 && (
                                <SizeStockDisplay>
                                  <SizeStockTitle>
                                    Stock po veličinama:
                                  </SizeStockTitle>
                                  <SizeStockList>
                                    {getProductSizeStocksWithAlerts(
                                      product
                                    ).map((stockData, index) => {
                                      return (
                                        <SizeStockBadge
                                          key={index}
                                          $alertLevel={stockData.alertLevel}
                                        >
                                          {stockData.displayText}
                                        </SizeStockBadge>
                                      );
                                    })}
                                  </SizeStockList>
                                </SizeStockDisplay>
                              )}

                              <StockAlert $status={stockStatus.status}>
                                <AlertIcon $status={stockStatus.status}>
                                  {stockStatus.status === "out" ||
                                  stockStatus.status === "low" ? (
                                    <FiAlertTriangle />
                                  ) : (
                                    <FiCheckCircle />
                                  )}
                                </AlertIcon>
                                {stockStatus.message}
                              </StockAlert>
                            </InventoryCard>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            textAlign: "center",
                            color: "#cccccc",
                            padding: "2rem",
                          }}
                        >
                          Bend nema proizvode
                        </div>
                      )}
                    </InventoryGrid>
                  </ChartContainer>
                </ChartsGrid>
              </>
            ) : (
              <div
                style={{
                  color: "#cccccc",
                  textAlign: "center",
                  padding: "3rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "10px",
                  border: "1px solid #444",
                }}
              >
                <FiAlertTriangle
                  size={48}
                  color="#f39c12"
                  style={{ marginBottom: "1rem" }}
                />
                <h3 style={{ color: "#ff6b35", marginBottom: "0.5rem" }}>
                  Nema dostupnih analytics podataka
                </h3>
                <p>Ovaj bend još uvek nema dovoljno podataka za analitiku.</p>
              </div>
            )}
          </AnalyticsContainer>
        )}
      </PageSection>
    </AdminContainer>
  );
};

export default AdminAnalyticsPage;
